import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Loader2, Search, Trash2, Wallet } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { api, formatApiError } from "../../lib/api";
import { Badge, EmptyRow, PanelTitle, StatCard, Table, fmtDate, fmtDateTime, money, shortHash } from "../office/ui";
import { NetworkView } from "../office/NetworkPanel";
import { CommissionsTable, LevelSummary } from "../office/EarningsPanel";
import { PayButton } from "./MlmCommissionsPanel";

const FILTERS = [["", "Todos"], ["activa", "Con membresía pagada"], ["pendiente", "Pendientes de pago"]];

export const DistributorDetail = ({ distId, onBack, onDeleted }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/admin/distributors/${distId}`).then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e))), [distId]);
  useEffect(() => {
    load();
  }, [load]);

  const remove = async () => {
    if (!window.confirm(`¿Eliminar a ${d.profile.name} (ID ${d.profile.dist_id})? Sus referidos pasarán a su patrocinador.`)) return;
    try {
      await api.delete(`/admin/distributors/${distId}`);
      toast.success("Distribuidor eliminado");
      onDeleted();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const payAll = async () => {
    const tx = window.prompt("Hash de la transacción de pago (opcional):", "") ?? null;
    if (tx === null) return;
    try {
      const { data } = await api.post(`/admin/commissions/pay-all/${distId}`, { tx_hash: tx });
      toast.success(`${data.paid} comisiones marcadas como pagadas`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!d) return <Loader2 className="h-6 w-6 animate-spin" />;
  const p = d.profile;
  return (
    <div data-testid="distributor-detail">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sm font-semibold text-ink/70 hover:text-ink" data-testid="distributor-back"><ArrowLeft className="h-4 w-4" /> Volver al listado</button>
      <div className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.14em] text-leaf-deep">ID {p.dist_id}</p>
          <h2 className="mt-1 font-display text-3xl font-semibold tracking-tight" data-testid="distributor-detail-name">{p.name}</h2>
          <p className="mt-2 flex flex-wrap items-center gap-3 text-sm text-ink/70">
            <a href={`https://wa.me/${p.phone}`} target="_blank" rel="noreferrer" className="text-leaf-deep underline-offset-2 hover:underline">{p.phone}</a>
            <span>{p.country}</span>
            <Badge status={p.membership_status} />
            <span>Registro {fmtDate(p.created_at)}</span>
            {p.membership_paid_at && <span>Pagó {fmtDate(p.membership_paid_at)}</span>}
          </p>
          <p className="mt-2 flex items-center gap-2 font-mono text-xs text-ink/70"><Wallet className="h-3.5 w-3.5" /> {p.wallet || "Sin wallet registrada"}</p>
          {d.upline.length > 0 && (
            <p className="mt-2 text-xs text-ink/60">Línea ascendente: {d.upline.map((u) => `${u.name} (${u.dist_id})`).join(" → ")}</p>
          )}
        </div>
        <div className="flex gap-2">
          {d.earnings.pending > 0 && p.membership_status === "activa" && (
            <button onClick={payAll} className="btn-leaf !px-5 !py-3 !text-sm" data-testid="distributor-pay-all">Pagar pendientes ({money(d.earnings.pending)})</button>
          )}
          <button onClick={remove} className="inline-flex items-center gap-2 rounded-full border border-coral/40 px-5 py-3 text-sm font-semibold text-coral hover:bg-coral/10" data-testid="distributor-delete"><Trash2 className="h-4 w-4" /> Eliminar</button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard dark label="Personas en su red" value={d.network.length} testId="detail-network" />
        <StatCard label="Ganancias generadas" value={money(d.earnings.total)} testId="detail-earnings" />
        <StatCard label="Pagado" value={money(d.earnings.paid)} testId="detail-paid" />
        <StatCard label="Pendiente" value={money(d.earnings.pending)} testId="detail-pending" />
      </div>

      <h3 className="mt-10 font-display text-xl font-semibold">Comprobantes de membresía</h3>
      <Table headers={["Fecha", "Moneda", "Hash", "Estado", "Verificación", "Revisado"]} testId="detail-payments" minWidth="min-w-[700px]">
        {!d.payments.length && <EmptyRow cols={6} text="Sin comprobantes enviados." />}
        {d.payments.map((pay) => (
          <tr key={pay.id} className="border-b border-ink/5 last:border-0">
            <td className="whitespace-nowrap px-4 py-3">{fmtDateTime(pay.created_at)}</td>
            <td className="px-4 py-3">{pay.token}</td>
            <td className="px-4 py-3 font-mono text-xs" title={pay.tx_hash}>{shortHash(pay.tx_hash)}</td>
            <td className="px-4 py-3"><Badge status={pay.status} /></td>
            <td className="px-4 py-3 text-xs">{pay.verification?.reason || "—"}</td>
            <td className="px-4 py-3 text-xs">{pay.reviewed_by || "—"} {pay.reviewed_at && fmtDate(pay.reviewed_at)}</td>
          </tr>
        ))}
      </Table>

      <h3 className="mt-10 font-display text-xl font-semibold">Comisiones</h3>
      <LevelSummary byLevel={d.earnings.by_level} />
      <CommissionsTable items={d.commissions} loading={false} testId="detail-commissions" actions={(c) => <PayButton c={c} onPaid={load} />} />

      <h3 className="mt-10 font-display text-xl font-semibold">Red / genealogía (5 niveles)</h3>
      <NetworkView root={p} network={d.network} loading={false} testId="detail-network" />
    </div>
  );
};

export const DistributorsPanel = () => {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/distributors", { params: { q, status } });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, status]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  if (selected) return <DistributorDetail distId={selected} onBack={() => setSelected(null)} onDeleted={() => { setSelected(null); load(); }} />;

  return (
    <div data-testid="distributors-panel">
      <PanelTitle title="Distribuidores" text="Busca por ID, nombre, celular o país. Haz clic en una fila para ver la ficha completa: red, comisiones y comprobantes." />
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/50" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ID, nombre, celular o país" className="h-11 bg-white pl-10" data-testid="distributors-search" />
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(([v, l]) => (
            <button key={v} onClick={() => setStatus(v)} className={`rounded-full px-3.5 py-2 text-xs font-semibold ${status === v ? "bg-ink text-white" : "border border-ink/15 bg-white hover:bg-paper"}`} data-testid={`distributors-filter-${v || "all"}`}>{l}</button>
          ))}
        </div>
      </div>
      <Table headers={["ID", "Nombre", "Celular", "País", "Patrocinador", "Membresía", "Pagó el", "Registro"]} testId="distributors-table">
        {loading && <tr><td colSpan={8} className="px-4 py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>}
        {!loading && !items.length && <EmptyRow cols={8} text="Sin distribuidores que coincidan." testId="distributors-empty" />}
        {items.map((d, i) => (
          <tr key={d.dist_id} onClick={() => setSelected(d.dist_id)} className="cursor-pointer border-b border-ink/5 transition-colors last:border-0 hover:bg-paper/60" data-testid={`distributor-row-${i}`}>
            <td className="px-4 py-3 font-mono text-xs font-semibold">{d.dist_id}</td>
            <td className="px-4 py-3 font-semibold">{d.name}</td>
            <td className="px-4 py-3">{d.phone}</td>
            <td className="px-4 py-3">{d.country}</td>
            <td className="px-4 py-3 font-mono text-xs">{d.sponsor_id || "—"}</td>
            <td className="px-4 py-3"><Badge status={d.membership_status} label={d.membership_status === "activa" ? "Pagada" : "Pendiente"} /></td>
            <td className="whitespace-nowrap px-4 py-3 text-ink/70">{fmtDate(d.membership_paid_at)}</td>
            <td className="whitespace-nowrap px-4 py-3 text-ink/70">{fmtDate(d.created_at)}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
};
